import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Box, Typography, Card, CardContent, Button, Alert, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { apiService } from '../services/apiService';

const TenantAddonPaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [addonCode, setAddonCode] = useState<string | null>(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      verifyPayment(sessionId);
    } else {
      setError('Missing session ID. Please check your payment confirmation email or contact support.');
      setLoading(false);
    }
  }, [sessionId]);

  const verifyPayment = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.verifyTenantAddonPayment(id);

      if (data.error) {
        setError(data.error);
        setLoading(false);
        return;
      }

      if (data.message) {
        setSuccess(true);
        if (data.addonCode) {
          setAddonCode(data.addonCode);
        }
        setLoading(false);

        setTimeout(() => {
          const token = localStorage.getItem('token');
          const user = localStorage.getItem('user');
          const dest = '/manage-account?tab=subscription&tenantAddonRefresh=true';
          if (token && user) {
            navigate(dest, { replace: true });
          } else {
            navigate(`/login?returnUrl=${encodeURIComponent(dest)}`, { replace: true });
          }
        }, 3000);
      } else {
        setError('Invalid response from server. Please contact support.');
        setLoading(false);
      }
    } catch (err: any) {
      const errorMsg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'An error occurred while verifying payment.';
      setError(errorMsg);
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ py: 8, textAlign: 'center' }}>
        {loading && !error && !success && (
          <Card>
            <CardContent>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Verifying payment...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please wait while we activate your add-on.
              </Typography>
            </CardContent>
          </Card>
        )}

        {success && (
          <Card>
            <CardContent>
              <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Payment successful!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {addonCode
                  ? `Your ${addonCode.replace(/_/g, ' ').toLowerCase()} add-on is now active.`
                  : 'Your add-on is now active.'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Redirecting to Manage Account...
              </Typography>
            </CardContent>
          </Card>
        )}

        {error && (
          <Card>
            <CardContent>
              <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
                {error}
              </Alert>
              <Button variant="contained" onClick={() => navigate('/manage-account?tab=subscription')}>
                Go to Subscription
              </Button>
            </CardContent>
          </Card>
        )}
      </Box>
    </Container>
  );
};

export default TenantAddonPaymentSuccess;
